/**
 * Chat History API — thin typed client for the Python chat-history service.
 *
 * The frontend keeps the existing dock-flyout DTO shape while translating the
 * Python conversation summary response.
 *
 *   POST   {base}/UserSession/GetAll                        → list conversations
 *   DELETE {base}/ChatHistoryAggregator/ChatHistory/{id}    → delete one
 *
 * `{base}` = `VITE_apiBaseUrl` (default http://localhost:50225/api/v2).
 * Auth: MSAL bearer token (silent acquisition) + `aiintegration-id` header.
 */

import api from './api';
import { environment, getStoredAiIntegrationId } from '../environments/environment';
import {
  msalInitPromise,
  msalInstance,
  apiTokenRequest,
  isInteractionRequiredError,
  redirectToReAuthenticate,
} from '../auth/msalConfig';

type ChatHistoryEnvironment = typeof environment & {
  apiBaseUrl?: string;
  aiIntegrationId?: string;
};

const chatHistoryEnvironment = environment as ChatHistoryEnvironment;

// ─── DTOs (mirror of Clara-AI-Services C# records) ──────────────────────────

/** Shape returned by `POST /UserSession/GetAll`. */
export interface UserChatSessionDTO {
  SessionId: string;
  Name: string;
  CreatedDate: string;
  ModifiedDate: string;
  LastChatDate: string;
  IsActive: boolean;
  ApplicationId: string;
  FolderId?: string | null;
  DataSourceId?: string;
  DataSourceName?: string;
  listDataSources?: string[];
  LogType?: string;
  Version?: string;
  IsPinned?: boolean;
}

export interface BulkDeleteResponse {
  Status: boolean;
  Results: Array<{ SessionId: string; IsDeleteSuccessful: boolean }>;
}

export interface DeleteAllChatSessionsResponse {
  Status: boolean;
  DeletedCount?: number;
  DeletedSessionIds?: string[];
}

interface DeleteUserSessionResult {
  SessionId?: string;
  IsDeleteSuccessful?: boolean;
}

interface ChatHistoryListResponse {
  items: Array<{
    session_id: string;
    title?: string | null;
    last_message_preview?: string | null;
    updated_at?: string | null;
    message_count?: number;
  }>;
  total_count: number;
  has_more: boolean;
}

type ChatHistoryListPayload = ChatHistoryListResponse | UserChatSessionDTO[] | Array<Record<string, unknown>>;

interface ValidSessionDTO {
  SessionId?: string;
  sessionId?: string;
}

interface SessionModelPayload {
  SessionIds: string[];
  Context: Record<string, never>;
}

interface SuggestedPayload {
  context: string;
}

type ChatDownloadType = 'chathistory' | 'sessionchathistory';

// ─── Errors ────────────────────────────────────────────────────────────────

export class ChatHistoryApiError extends Error {
  readonly status: number;
  constructor(message: string, status = 0) {
    super(message);
    this.name = 'ChatHistoryApiError';
    this.status = status;
  }
}

function resolveChatHistoryApiBaseUrl(): string {
  const configuredBaseUrl = firstNonEmptyString(
    import.meta.env.VITE_apiBaseUrl as string | undefined,
    chatHistoryEnvironment.apiBaseUrl,
    chatHistoryEnvironment.APIServiceUrl,
  );

  return normalizeChatHistoryApiBaseUrl(configuredBaseUrl );
}

function resolveChatHistoryAiIntegrationId(): string {
  return firstNonEmptyString(
    getStoredAiIntegrationId(),
    import.meta.env.VITE_aiintegrationId as string | undefined,
    chatHistoryEnvironment.aiIntegrationId,
  );
}

function normalizeChatHistoryApiBaseUrl(rawBaseUrl: string): string {
  const parsedUrl = new URL(rawBaseUrl, window.location.origin);
  const normalizedPath = parsedUrl.pathname.replace(/\/+$/, '');

  // Clara API expects /api/v2 routes; append when env points at host/root only.
  if (/\/api\/v\d+$/i.test(normalizedPath)) {
    parsedUrl.pathname = normalizedPath;
  } else {
    parsedUrl.pathname = `${normalizedPath}/api/v2`;
  }

  return parsedUrl.toString();
}

const CHAT_HISTORY_API_BASE_URL = resolveChatHistoryApiBaseUrl();

const CHAT_HISTORY_API_ROOT = new URL(CHAT_HISTORY_API_BASE_URL, window.location.origin).toString().replace(/\/$/, '');

function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function getStoredClaraContext(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  return firstNonEmptyString(
    window.sessionStorage.getItem('contextFromConsumer'),
    window.sessionStorage.getItem('Context'),
  );
}

function getSerializedClaraContext(): string {
  const storedContext = getStoredClaraContext();
  if (!storedContext) {
    return '[]';
  }

  try {
    return JSON.stringify(JSON.parse(storedContext) as unknown);
  } catch {
    return storedContext;
  }
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildChatDownloadPayload(type: ChatDownloadType, sessionId?: string): {
  SessionId: string;
  Type: ChatDownloadType;
  Context: string;
} {
  return {
    SessionId: sessionId?.trim() ?? '',
    Type: type,
    Context: getSerializedClaraContext(),
  };
}

async function buildChatHistoryHeaders(): Promise<Record<string, string>> {
  await msalInitPromise;

  const activeAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
  const claims = (activeAccount?.idTokenClaims ?? {}) as Record<string, unknown>;

  const userId = firstNonEmptyString(
    activeAccount?.username,
    claims.preferred_username,
    claims.upn,
    claims.email,
    claims.unique_name,
    claims.oid,
    claims.sub,
    activeAccount?.localAccountId,
  );
  const tenantId = firstNonEmptyString(claims.tid, claims.tenantId);

  if (!userId) {
    throw new ChatHistoryApiError('Chat History API requires a signed-in user id');
  }

  if (!tenantId) {
    throw new ChatHistoryApiError('Chat History API requires a tenant id');
  }

  const aiIntegrationId = resolveChatHistoryAiIntegrationId();

  return {
    'x-user-id': userId,
    'x-tenant-id': tenantId,
    userId,
    tenantId,
    ...(aiIntegrationId ? { aiintegrationid: aiIntegrationId } : {}),
  };
}

async function buildChatHistoryAuthHeader(): Promise<Record<string, string>> {
  await msalInitPromise;

  const activeAccount = msalInstance.getActiveAccount();
  if (!activeAccount) {
    return {};
  }

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account: activeAccount });
    return { Authorization: `Bearer ${tokenResponse.accessToken}` };
  } catch (error) {
    if (isInteractionRequiredError(error)) {
      await redirectToReAuthenticate();
    }
    return {};
  }
}

async function authorizedFetch<T>(
  path: string,
  init: RequestInit = {},
  baseRoot = CHAT_HISTORY_API_ROOT,
  includeBearerToken = false,
): Promise<T> {
  try {
    const authHeader = includeBearerToken ? await buildChatHistoryAuthHeader() : {};

    return await api.fetch<T>(`${baseRoot}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers as Record<string, string> | undefined),
        ...authHeader,
        ...(await buildChatHistoryHeaders()),
      },
    });
  } catch (error) {
    if (error instanceof ChatHistoryApiError) {
      throw error;
    }

    throw new ChatHistoryApiError(
      error instanceof Error ? error.message : `Chat History API request failed: ${path}`,
    );
  }
}

async function authorizedBlobFetch(path: string, init: RequestInit = {}, baseRoot = CHAT_HISTORY_API_ROOT): Promise<Blob> {
  const authHeader = await buildChatHistoryAuthHeader();

  const response = await fetch(`${baseRoot}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
      ...authHeader,
      ...(await buildChatHistoryHeaders()),
    },
  });

  if (!response.ok) {
    throw new ChatHistoryApiError(`Chat History API error: ${response.statusText}`, response.status);
  }

  return response.blob();
}

async function getValidSessionIds(sessionIds: string[]): Promise<Set<string>> {
  if (sessionIds.length === 0) {
    return new Set<string>();
  }

  const payload: SessionModelPayload = {
    SessionIds: sessionIds,
    Context: {},
  };

  const validSessions = await authorizedFetch<ValidSessionDTO[]>('/ChatHistoryAggregator/GetValidSessions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  const ids = Array.isArray(validSessions)
    ? validSessions
      .map((session) => String(session.SessionId ?? session.sessionId ?? '').trim())
      .filter(Boolean)
    : [];

  return new Set(ids);
}

export async function getSuggestedPrompts(): Promise<unknown> {
  const payload: SuggestedPayload = {
    context: getSerializedClaraContext(),
  };

  return authorizedFetch<unknown>('/ApplicationPromptLibaries/SuggestedPrompt', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getSuggestedAgents(): Promise<unknown> {
  const payload: SuggestedPayload = {
    context: getSerializedClaraContext(),
  };

  return authorizedFetch<unknown>('/Agent/SuggestedAgent', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

async function refreshChatHistoryDeleteDependencies(): Promise<void> {
  await Promise.allSettled([
    getSuggestedPrompts(),
    getSuggestedAgents(),
    getUserChatSessions(),
  ]);
}

function normalizeChatHistorySessions(payload: ChatHistoryListPayload): UserChatSessionDTO[] {
  const normalizeOptionalString = (value: unknown): string | undefined => {
    if (value === null || value === undefined) {
      return undefined;
    }

    const normalized = String(value).trim();
    return normalized || undefined;
  };

  const normalizeDataSourceList = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value)) {
      return undefined;
    }

    return value
      .map((item) => String(item ?? '').trim())
      .filter(Boolean);
  };

  const fromLegacyArray = (item: Record<string, unknown>): UserChatSessionDTO => ({
    SessionId: String(item.SessionId ?? item.session_id ?? item.sessionId ?? ''),
    Name: String(item.Name ?? item.title ?? item.last_message_preview ?? 'Untitled chat'),
    CreatedDate: String(item.CreatedDate ?? item.created_at ?? item.updated_at ?? new Date().toISOString()),
    ModifiedDate: String(item.ModifiedDate ?? item.updated_at ?? item.created_at ?? new Date().toISOString()),
    LastChatDate: String(item.LastChatDate ?? item.last_chat_date ?? item.updated_at ?? item.created_at ?? new Date().toISOString()),
    IsActive: item.IsActive !== false,
    ApplicationId: String(item.ApplicationId ?? item.application_id ?? 'tru-clara-ai'),
    FolderId: (item.FolderId as string | null | undefined) ?? null,
    DataSourceId: normalizeOptionalString(item.DataSourceId ?? item.dataSourceId),
    DataSourceName: normalizeOptionalString(item.DataSourceName ?? item.dataSourceName),
    listDataSources: normalizeDataSourceList(item.listDataSources ?? item.ListDataSources),
    LogType: normalizeOptionalString(item.LogType),
    Version: normalizeOptionalString(item.Version),
    IsPinned: item.IsPinned === true || item.isPinned === true,
  });

  if (Array.isArray(payload)) {
    return payload.map((item) => fromLegacyArray(item as Record<string, unknown>)).filter((item) => item.SessionId);
  }

  if (Array.isArray(payload.items)) {
    return payload.items.map((item) => {
      const timestamp = item.updated_at || new Date().toISOString();
      return {
        SessionId: item.session_id,
        Name: item.title?.trim() || item.last_message_preview?.trim() || 'Untitled chat',
        CreatedDate: timestamp,
        ModifiedDate: timestamp,
        LastChatDate: timestamp,
        IsActive: true,
        ApplicationId: 'tru-clara-ai',
        FolderId: null,
        DataSourceId: undefined,
        DataSourceName: undefined,
        listDataSources: [],
        LogType: undefined,
        Version: undefined,
        IsPinned: false,
      } satisfies UserChatSessionDTO;
    });
  }

  return [];
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Fetch every chat session belonging to the current user.
 */
export async function getUserChatSessions(
  limit = 100,
  offset = 0,
): Promise<UserChatSessionDTO[]> {
  const response = await authorizedFetch<ChatHistoryListPayload>('/UserSession/GetAll', {
    method: 'POST',
    body: JSON.stringify({ limit, offset }),
  });

  const sessions = normalizeChatHistorySessions(response);
  const sessionIds = sessions.map((session) => session.SessionId).filter(Boolean);

  try {
    const validSessionIds = await getValidSessionIds(sessionIds);
    if (validSessionIds.size === 0) {
      return [];
    }
    return sessions.filter((session) => validSessionIds.has(session.SessionId));
  } catch {
    // Keep chat history usable if valid-session filtering endpoint is transiently unavailable.
    return sessions;
  }
}

export async function renameChatSession(sessionId: string, name: string): Promise<boolean> {
  const normalizedName = name.trim();
  if (!sessionId.trim() || !normalizedName) {
    throw new ChatHistoryApiError('Session id and name are required to rename chat session');
  }

  const payload: Partial<UserChatSessionDTO> = {
    SessionId: sessionId,
    Name: normalizedName,
    ModifiedDate: new Date().toISOString(),
  };

  const result = await authorizedFetch<boolean>('/ChatHistoryAggregator/Rename', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

  return result === true;
}

export async function updateChatSessionPin(sessionId: string, isPinned: boolean): Promise<boolean> {
  if (!sessionId.trim()) {
    throw new ChatHistoryApiError('Session id is required to update its pin state');
  }

  return authorizedFetch<boolean>('/UserSession/Pin', {
    method: 'PUT',
    body: JSON.stringify({ SessionId: sessionId, IsPinned: isPinned }),
  });
}

/** Delete a single chat session by id. */
export async function deleteChatSession(sessionId: string, options?: { refreshRelatedData?: boolean }): Promise<boolean> {
  if (!sessionId.trim()) {
    throw new ChatHistoryApiError('Session id is required');
  }

  await authorizedFetch<void>(`/ChatHistoryAggregator/ChatHistory/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });

  if (options?.refreshRelatedData !== false) {
    await refreshChatHistoryDeleteDependencies();
  }

  return true;
}

/**
 * Delete multiple chat sessions in one call.
 * Backend returns a per-session success map; we resolve `true` only when
 * every id succeeds.
 */
export async function bulkDeleteChatSessions(
  sessionIds: string[],
): Promise<BulkDeleteResponse> {
  const normalizedSessionIds = sessionIds.map((id) => id.trim()).filter(Boolean);
  if (normalizedSessionIds.length === 0) {
    return { Status: true, Results: [] };
  }

  const response = await authorizedFetch<unknown>('/UserSession/ChatHistory/Bulk', {
    method: 'DELETE',
    body: JSON.stringify({
      SessionIds: normalizedSessionIds,
      Context: getSerializedClaraContext(),
    }),
  });

  const responseResults = Array.isArray(response)
    ? response
    : typeof response === 'object' && response !== null && Array.isArray((response as BulkDeleteResponse).Results)
      ? (response as BulkDeleteResponse).Results
      : [];
  const resultById = new Map(
    responseResults.map((result) => [String(result.SessionId ?? ''), result.IsDeleteSuccessful === true]),
  );
  const results = normalizedSessionIds.map((sessionId) => ({
    SessionId: sessionId,
    IsDeleteSuccessful: resultById.get(sessionId) === true,
  }));

  if (results.some((result) => result.IsDeleteSuccessful)) {
    await refreshChatHistoryDeleteDependencies();
  }

  return {
    Status: results.every((result) => result.IsDeleteSuccessful),
    Results: results,
  };
}

/**
 * Delete all chat sessions for the current user.
 *
 * Primary route: `DELETE /UserSession/DeleteAll`
 * Some environments may still expose the same operation as POST.
 */
export async function deleteAllChatSessions(): Promise<DeleteAllChatSessionsResponse> {
  // Backend DeleteAll expects the body itself to be a JSON array (JArray.Parse).
  const deleteAllPayload: Array<{ key: string; value: string }> = [];
  const unpinnedSessionIds = await getUserChatSessions()
    .then((sessions) => sessions
      .filter((session) => session.IsPinned !== true)
      .map((session) => session.SessionId)
      .filter(Boolean))
    .catch(() => [] as string[]);

  const parseDeleteAllResponse = (payload: unknown): DeleteAllChatSessionsResponse => {
    if (Array.isArray(payload)) {
      const results = payload as DeleteUserSessionResult[];
      const total = results.length;
      const deletedCount = results.filter((item) => item.IsDeleteSuccessful === true).length;
      const deletedSessionIds = results
        .filter((item) => item.IsDeleteSuccessful === true)
        .map((item) => String(item.SessionId ?? '').trim())
        .filter(Boolean);

      return {
        Status: total === 0 ? true : deletedCount === total,
        DeletedCount: deletedCount,
        DeletedSessionIds: deletedSessionIds.length > 0 ? deletedSessionIds : unpinnedSessionIds,
      };
    }

    if (typeof payload === 'boolean') {
      return {
        Status: payload,
        DeletedSessionIds: payload ? unpinnedSessionIds : [],
      };
    }

    if (payload && typeof payload === 'object') {
      const candidate = payload as Record<string, unknown>;
      const deletedCount = Number(candidate.DeletedCount ?? candidate.deletedCount);
      const explicitStatus = candidate.Status ?? candidate.status;
      const status = explicitStatus === undefined ? true : explicitStatus === true;
      return {
        Status: status,
        DeletedCount: Number.isFinite(deletedCount) ? deletedCount : undefined,
        DeletedSessionIds: status ? unpinnedSessionIds : [],
      };
    }

    // A successful DELETE may legitimately return 204 No Content. Errors are
    // thrown by authorizedFetch, so reaching here still represents success.
    return { Status: true, DeletedSessionIds: unpinnedSessionIds };
  };

  let response: unknown;

  try {
    response = await authorizedFetch<unknown>('/UserSession/DeleteAll', {
      method: 'DELETE',
      body: JSON.stringify(deleteAllPayload),
    });
  } catch (error) {
    const isMethodOrRouteMismatch =
      error instanceof ChatHistoryApiError && (error.status === 400 || error.status === 404 || error.status === 405);

    if (!isMethodOrRouteMismatch) {
      throw error;
    }

    response = await authorizedFetch<unknown>('/UserSession/DeleteAll', {
      method: 'POST',
      body: JSON.stringify(deleteAllPayload),
    });
  }

  const parsed = parseDeleteAllResponse(response);

  if (parsed.Status) {
    await refreshChatHistoryDeleteDependencies();
  }

  return parsed;
}

/** Fetch the full message history for a single session. */
export async function getChatSession(sessionId: string): Promise<unknown> {
  if (!sessionId.trim()) {
    throw new ChatHistoryApiError('Session id is required');
  }

  return authorizedFetch<unknown>(`/ChatHistoryAggregator/Session/${encodeURIComponent(sessionId)}/`, {
    method: 'GET',
  });
}

/**
 * Download the chat history for a session as a file.
 * Triggers a browser file-save dialog.
 */
export async function downloadChat(sessionId: string, filename = 'chat-history.xlsx'): Promise<void> {
  if (!sessionId.trim()) {
    throw new ChatHistoryApiError('Session id is required');
  }

  const blob = await authorizedBlobFetch('/ChatHistoryAggregator/DownloadChat', {
    method: 'POST',
    body: JSON.stringify(buildChatDownloadPayload('sessionchathistory', sessionId)),
  });

  triggerBlobDownload(blob, filename);
}

export async function downloadSelectedChats(
  sessionIds: string[],
  filename = 'chat-history.xlsx',
): Promise<void> {
  const normalizedSessionIds = sessionIds.map((id) => id.trim()).filter(Boolean);
  if (normalizedSessionIds.length === 0) {
    throw new ChatHistoryApiError('At least one session id is required');
  }

  const blob = await authorizedBlobFetch('/ChatHistoryAggregator/DownloadChat', {
    method: 'POST',
    body: JSON.stringify(
      buildChatDownloadPayload('sessionchathistory', normalizedSessionIds.join(',')),
    ),
  });

  triggerBlobDownload(blob, filename);
}

export async function downloadAllChats(filename = 'chat-history.xlsx'): Promise<void> {
  const blob = await authorizedBlobFetch('/ChatHistoryAggregator/DownloadChat', {
    method: 'POST',
    body: JSON.stringify(buildChatDownloadPayload('chathistory')),
  });

  triggerBlobDownload(blob, filename);
}

export async function downloadAllUploadedFiles(filename = 'uploaded-files.zip'): Promise<void> {
  const blob = await authorizedBlobFetch('/ChatHistoryAggregator/DownloadAllUploadedFiles', {
    method: 'POST',
    body: JSON.stringify({
      context: getSerializedClaraContext(),
    }),
  });

  triggerBlobDownload(blob, filename);
}

/**
 * Copy the chat history for a session to the clipboard as plain text.
 */
export async function copyChat(sessionId: string): Promise<void> {
  if (!sessionId.trim()) {
    throw new ChatHistoryApiError('Session id is required');
  }

  const blob = await authorizedBlobFetch('/ChatHistoryAggregator/DownloadChat', {
    method: 'POST',
    body: JSON.stringify(buildChatDownloadPayload('sessionchathistory', sessionId)),
  });

  const text = await blob.text();
  await navigator.clipboard.writeText(text);
}

// ─── Retention Policy ────────────────────────────────────────────────────

export interface RetentionPolicyResponse {
  Status: boolean;
  RetentionPolicy: {
    Interval: number;
    Unit: string;
  };
  RuleName: string;
}

/**
 * Fetch the MF retention policy from UserSession controller.
 * Passes the session context as payload (same shape as GetContext() in the Angular layer).
 * Returns null on any error — callers must treat null as "no retention limit".
 */
export async function getMFRetentionPeriod(): Promise<RetentionPolicyResponse | null> {
  try {
    const context =
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('Context') : null) ?? '';
    return await authorizedFetch<RetentionPolicyResponse>(
      '/UserSession/GetMFRetentionPeriodFromRuleConfig',
      {
        method: 'POST',
        body: context,
      },
    );
  } catch {
    return null;
  }
}
export const chatHistoryApi = {
  getUserChatSessions,
  getSuggestedPrompts,
  getSuggestedAgents,
  renameChatSession,
  updateChatSessionPin,
  deleteChatSession,
  bulkDeleteChatSessions,
  getMFRetentionPeriod,
  getChatSession,
  downloadChat,
  downloadSelectedChats,
  downloadAllChats,
  downloadAllUploadedFiles,
  copyChat,
} as const;

export default chatHistoryApi;
